import { useMemo } from 'react';
import { useTranslation } from '@stores/translationStore';
import './StatusCard.scss';

export interface StatusCardMetadata {
  type: 'order' | 'housekeeping' | 'maintenance' | 'reservation' | 'service' | 'laundry';
  referenceId: string;
  orderNumber?: string;
  status: string;
  updatedAt?: string;
  // Order fields
  stationName?: string;
  items?: { name: string; quantity: number; price?: number }[];
  totalAmount?: number;
  // Housekeeping fields
  requestType?: string;
  itemName?: string;
  quantity?: number;
  roomNumber?: string;
  // Maintenance fields
  subject?: string;
  category?: string;
  priority?: string;
  // Reservation fields
  reservationType?: string;
  date?: string;
  time?: string;
  partySize?: number;
  specialRequests?: string;
  rejectionReason?: string;
  // Service request fields
  description?: string;
  scheduledFor?: string;
  // Laundry fields
  isExpress?: boolean;
  expressSurcharge?: number;
  currency?: string;
  // Device/person fields
  requestedBy?: string;
}

type TFn = (key: string, params?: Record<string, string | number>) => string;

// Status step configurations per type — must match admin-side workflows
const ORDER_STEPS = ['Pending', 'Preparing', 'Ready', 'Delivered'];
const HOUSEKEEPING_ITEM_STEPS = ['Pending', 'InProgress', 'Ready', 'Delivered'];
const HOUSEKEEPING_CLEANING_STEPS = ['Pending', 'Started', 'Completed'];
const MAINTENANCE_STEPS = ['Reported', 'Acknowledged', 'InProgress', 'Resolved'];
const RESERVATION_STEPS = ['Pending', 'Confirmed'];
const SERVICE_STEPS = ['Pending', 'Acknowledged', 'InProgress', 'Completed'];
const LAUNDRY_STEPS = ['Pending', 'Acknowledged', 'Sent', 'Received', 'Delivered'];

const STATUS_ICONS: Record<string, string> = {
  // Order
  Pending: '⏳',
  Confirmed: '✓',
  Preparing: '👨‍🍳',
  Ready: '🔔',
  Delivered: '✅',
  // Housekeeping
  InProgress: '🔄',
  Started: '🔄',
  Completed: '✅',
  // Maintenance
  Reported: '📋',
  Acknowledged: '👀',
  Resolved: '✅',
  // Laundry
  Sent: '🚚',
  Received: '📦',
  // Reservation
  Cancelled: '❌',
  Declined: '❌',
};

// label values are translation keys (resolved via t())
const TYPE_CONFIG: Record<string, { label: string; icon: string; colorClass: string }> = {
  order: { label: 'status_card.type.order', icon: '🍽️', colorClass: 'status-card--order' },
  housekeeping: { label: 'status_card.type.housekeeping', icon: '🛎️', colorClass: 'status-card--housekeeping' },
  maintenance: { label: 'status_card.type.maintenance', icon: '🔧', colorClass: 'status-card--maintenance' },
  reservation: { label: 'status_card.type.reservation', icon: '📅', colorClass: 'status-card--reservation' },
  service: { label: 'status_card.type.service', icon: '🔔', colorClass: 'status-card--service' },
  laundry: { label: 'status_card.type.laundry', icon: '👔', colorClass: 'status-card--laundry' },
};

// requestType -> translation key
const HOUSEKEEPING_TYPE_KEYS: Record<string, string> = {
  room_cleaning: 'status_card.hk.roomCleaning',
  turndown: 'status_card.hk.turndown',
  item_request: 'status_card.hk.itemRequest',
};

// service category -> translation key
const SERVICE_CATEGORY_KEYS: Record<string, string> = {
  taxi: 'status_card.svc.taxi',
  transport: 'status_card.svc.transport',
  wake_up_call: 'status_card.svc.wakeUpCall',
  luggage: 'status_card.svc.luggage',
  room_service: 'status_card.svc.roomService',
  concierge: 'status_card.svc.concierge',
  special_request: 'status_card.svc.specialRequest',
  information_request: 'status_card.svc.informationRequest',
  other: 'status_card.svc.other',
};

// backend status / step name -> translation key
const STEP_KEYS: Record<string, string> = {
  Pending: 'status_card.step.pending',
  Preparing: 'status_card.step.preparing',
  Ready: 'status_card.step.ready',
  Delivered: 'status_card.step.delivered',
  Confirmed: 'status_card.step.confirmed',
  InProgress: 'status_card.step.inProgress',
  Started: 'status_card.step.started',
  Completed: 'status_card.step.completed',
  Reported: 'status_card.step.reported',
  Acknowledged: 'status_card.step.acknowledged',
  Resolved: 'status_card.step.resolved',
  Sent: 'status_card.step.sent',
  Received: 'status_card.step.received',
  Cancelled: 'status_card.step.cancelled',
  Declined: 'status_card.step.declined',
};

// Translate a step/status name, falling back to spacing camelCase if unknown
function stepLabel(t: TFn, step: string): string {
  const key = STEP_KEYS[step];
  return key ? t(key) : step.replace(/([A-Z])/g, ' $1').trim();
}

interface StatusCardProps {
  metadata: StatusCardMetadata;
  onEditOrder?: (referenceId: string, stationName?: string) => void;
}

export default function StatusCard({ metadata, onEditOrder }: StatusCardProps) {
  const { t } = useTranslation();
  const config = TYPE_CONFIG[metadata.type] || TYPE_CONFIG.order;

  const isCleaning = metadata.type === 'housekeeping' &&
    (metadata.requestType === 'room_cleaning' || metadata.requestType === 'turndown');

  const steps = useMemo(() => {
    switch (metadata.type) {
      case 'order': return ORDER_STEPS;
      case 'housekeeping': return isCleaning ? HOUSEKEEPING_CLEANING_STEPS : HOUSEKEEPING_ITEM_STEPS;
      case 'maintenance': return MAINTENANCE_STEPS;
      case 'reservation': return RESERVATION_STEPS;
      case 'service': return SERVICE_STEPS;
      case 'laundry': return LAUNDRY_STEPS;
      default: return ORDER_STEPS;
    }
  }, [metadata.type, isCleaning]);

  // Normalize status for display — map backend status strings to step names
  const normalizedStatus = useMemo(() => {
    const s = metadata.status;

    if (metadata.type === 'housekeeping') {
      if (isCleaning) {
        // Cleaning/turndown: 3-step flow (Pending → Started → Completed)
        if (s === 'InProgress') return 'Started';
        if (s === 'Delivered' || s === 'Completed' || s === 'Ready') return 'Completed';
      } else {
        // Item requests: 4-step flow (Pending → InProgress → Ready → Delivered)
        if (s === 'Completed') return 'Delivered';
      }
    }

    // Maintenance: backend sends "In Progress" (with space)
    if (metadata.type === 'maintenance' && s === 'In Progress') return 'InProgress';

    // Service requests: backend sends "In Progress" (with space)
    if (metadata.type === 'service' && s === 'In Progress') return 'InProgress';

    // Orders: "Confirmed" maps to "Preparing" since kitchen skips Confirmed step
    if (metadata.type === 'order' && s === 'Confirmed') return 'Preparing';

    return s;
  }, [metadata.type, metadata.status, isCleaning]);

  const currentStepIndex = steps.findIndex(
    s => s.toLowerCase() === normalizedStatus.toLowerCase()
  );

  const isCancelled = ['Cancelled', 'Declined'].includes(metadata.status);

  // Terminal (done) statuses — show compact completed card
  const isTerminal = useMemo(() => {
    const s = metadata.status;
    if (isCancelled) return false; // cancelled is separate
    if (metadata.type === 'order') return ['Delivered', 'Completed'].includes(s);
    if (metadata.type === 'housekeeping') return ['Delivered', 'Completed'].includes(s);
    if (metadata.type === 'maintenance') return s === 'Resolved';
    if (metadata.type === 'reservation') return s === 'Confirmed';
    if (metadata.type === 'service') return s === 'Completed';
    if (metadata.type === 'laundry') return s === 'Delivered';
    return false;
  }, [metadata.type, metadata.status, isCancelled]);

  // Build a short summary for completed state
  const completedSummary = useMemo(() => {
    if (metadata.type === 'order') {
      const itemList = metadata.items?.map(i => `${i.quantity}× ${i.name}`).join(', ');
      return itemList || metadata.stationName || t('status_card.summary.order');
    }
    if (metadata.type === 'housekeeping') {
      if (metadata.itemName) return `${metadata.itemName}${metadata.quantity && metadata.quantity > 1 ? ` × ${metadata.quantity}` : ''}`;
      return metadata.requestType === 'room_cleaning' ? t('status_card.hk.roomCleaning') : metadata.requestType === 'turndown' ? t('status_card.hk.turndown') : t('status_card.summary.request');
    }
    if (metadata.type === 'maintenance') return metadata.subject || t('status_card.summary.maintenance');
    if (metadata.type === 'reservation') return `${metadata.reservationType || t('status_card.summary.reservation')}${metadata.time ? ` at ${metadata.time}` : ''}`;
    if (metadata.type === 'service') return metadata.description ? (metadata.description.length > 60 ? metadata.description.slice(0, 60) + '…' : metadata.description) : t('status_card.summary.service');
    if (metadata.type === 'laundry') {
      const itemList = metadata.items?.map(i => `${i.quantity}× ${i.name}`).join(', ');
      return `${itemList || t('status_card.summary.laundry')}${metadata.isExpress ? ' ⚡' : ''}`;
    }
    return t('status_card.summary.request');
  }, [metadata, t]);

  const formatCompletedTime = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // ── Compact completed card ──
  if (isTerminal) {
    const doneLabel = metadata.type === 'maintenance' ? t('status_card.step.resolved') : stepLabel(t, normalizedStatus);
    return (
      <div className={`status-card status-card--done ${config.colorClass}`}>
        <div className="status-card__done-row">
          <span className="status-card__done-icon">✅</span>
          <div className="status-card__done-info">
            <span className="status-card__done-label">{config.icon} {metadata.type === 'order' && metadata.stationName ? metadata.stationName : t(config.label)} — {doneLabel}</span>
            <span className="status-card__done-summary">{completedSummary}</span>
          </div>
          {metadata.updatedAt && (
            <span className="status-card__done-time">{formatCompletedTime(metadata.updatedAt)}</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`status-card ${config.colorClass} ${isCancelled ? 'status-card--cancelled' : ''}`}>
      {/* Header */}
      <div className="status-card__header">
        <span className="status-card__type-icon">{config.icon}</span>
        <span className="status-card__type-label">{metadata.type === 'order' && metadata.stationName ? metadata.stationName : t(config.label)}</span>
        {metadata.orderNumber && (
          <span className="status-card__order-number">#{metadata.orderNumber}</span>
        )}
        <span className="status-card__status-badge">
          {STATUS_ICONS[normalizedStatus] || '⏳'} {stepLabel(t, normalizedStatus)}
        </span>
      </div>

      {/* Content based on type */}
      <div className="status-card__body">
        {metadata.type === 'order' && (
          <OrderCardBody metadata={metadata} />
        )}
        {metadata.type === 'housekeeping' && (
          <HousekeepingCardBody metadata={metadata} />
        )}
        {metadata.type === 'maintenance' && (
          <MaintenanceCardBody metadata={metadata} />
        )}
        {metadata.type === 'reservation' && (
          <ReservationCardBody metadata={metadata} />
        )}
        {metadata.type === 'service' && (
          <ServiceCardBody metadata={metadata} />
        )}
        {metadata.type === 'laundry' && (
          <LaundryCardBody metadata={metadata} />
        )}

        {/* Edit button for Pending orders */}
        {metadata.type === 'order' && metadata.status === 'Pending' && onEditOrder && (
          <button
            className="status-card__edit-btn"
            onClick={() => onEditOrder(metadata.referenceId, metadata.stationName)}
          >
            ✏️ {t('status_card.editOrder')}
          </button>
        )}
      </div>

      {/* Progress steps */}
      {!isCancelled && (
        <div className="status-card__progress">
          {steps.map((step, i) => (
            <div
              key={step}
              className={`status-card__step ${
                i <= currentStepIndex ? 'status-card__step--completed' : ''
              } ${i === currentStepIndex ? 'status-card__step--active' : ''}`}
            >
              <div className="status-card__step-dot">
                {i < currentStepIndex ? '✓' : i === currentStepIndex ? STATUS_ICONS[step] || '●' : ''}
              </div>
              <span className="status-card__step-label">{stepLabel(t, step)}</span>
              {i < steps.length - 1 && <div className="status-card__step-line" />}
            </div>
          ))}
        </div>
      )}

      {isCancelled && (
        <div className="status-card__cancelled-badge">
          ❌ {stepLabel(t, metadata.status)}
          {metadata.rejectionReason && (
            <span className="status-card__rejection-reason">{t('status_card.reason')}: {metadata.rejectionReason}</span>
          )}
        </div>
      )}
    </div>
  );
}

function OrderCardBody({ metadata }: { metadata: StatusCardMetadata }) {
  const { t } = useTranslation();
  const sym = metadata.currency || '₪';
  return (
    <>
      {metadata.stationName && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.station')}</span>
          <span className="status-card__detail-value">{metadata.stationName}</span>
        </div>
      )}
      {metadata.items && metadata.items.length > 0 && (
        <div className="status-card__items">
          {metadata.items.map((item, i) => (
            <div key={i} className="status-card__item">
              <span>{item.quantity}× {item.name}</span>
              {item.price != null && <span className="status-card__item-price">{sym}{item.price.toFixed(2)}</span>}
            </div>
          ))}
        </div>
      )}
      {metadata.totalAmount != null && (
        <div className="status-card__total">
          <span>{t('common.total')}</span>
          <span>{sym}{metadata.totalAmount.toFixed(2)}</span>
        </div>
      )}
    </>
  );
}

function HousekeepingCardBody({ metadata }: { metadata: StatusCardMetadata }) {
  const { t } = useTranslation();
  const typeKey = metadata.requestType ? HOUSEKEEPING_TYPE_KEYS[metadata.requestType] : undefined;
  return (
    <>
      <div className="status-card__detail">
        <span className="status-card__detail-label">{t('status_card.type')}</span>
        <span className="status-card__detail-value">
          {typeKey ? t(typeKey) : (metadata.requestType || t('status_card.request'))}
        </span>
      </div>
      {metadata.itemName && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.item')}</span>
          <span className="status-card__detail-value">
            {metadata.itemName}{metadata.quantity && metadata.quantity > 1 ? ` × ${metadata.quantity}` : ''}
          </span>
        </div>
      )}
      {metadata.roomNumber && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('common.room')}</span>
          <span className="status-card__detail-value">{metadata.roomNumber}</span>
        </div>
      )}
    </>
  );
}

function MaintenanceCardBody({ metadata }: { metadata: StatusCardMetadata }) {
  const { t } = useTranslation();
  return (
    <>
      {metadata.subject && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.issue')}</span>
          <span className="status-card__detail-value">{metadata.subject}</span>
        </div>
      )}
      {metadata.category && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('common.category')}</span>
          <span className="status-card__detail-value" style={{ textTransform: 'capitalize' }}>{metadata.category}</span>
        </div>
      )}
      {metadata.priority && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('common.priority')}</span>
          <span className={`status-card__priority status-card__priority--${metadata.priority}`}>
            {metadata.priority}
          </span>
        </div>
      )}
    </>
  );
}

function ReservationCardBody({ metadata }: { metadata: StatusCardMetadata }) {
  const { t } = useTranslation();
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <>
      {metadata.reservationType && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.type')}</span>
          <span className="status-card__detail-value" style={{ textTransform: 'capitalize' }}>{metadata.reservationType}</span>
        </div>
      )}
      {metadata.date && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('common.date')}</span>
          <span className="status-card__detail-value">{formatDate(metadata.date)}{metadata.time ? ` at ${metadata.time}` : ''}</span>
        </div>
      )}
      {metadata.partySize && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.partySize')}</span>
          <span className="status-card__detail-value">{metadata.partySize} {metadata.partySize === 1 ? t('status_card.guest') : t('status_card.guests')}</span>
        </div>
      )}
      {metadata.totalAmount != null && metadata.totalAmount > 0 && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.quotedPrice')}</span>
          <span className="status-card__detail-value" style={{ fontWeight: 600 }}>
            {metadata.currency || ''} {metadata.totalAmount.toFixed(2)}
          </span>
        </div>
      )}
    </>
  );
}

function ServiceCardBody({ metadata }: { metadata: StatusCardMetadata }) {
  const { t } = useTranslation();
  const catKey = metadata.category ? SERVICE_CATEGORY_KEYS[metadata.category] : undefined;
  return (
    <>
      {metadata.category && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('common.category')}</span>
          <span className="status-card__detail-value">
            {catKey ? t(catKey) : metadata.category.replace(/_/g, ' ')}
          </span>
        </div>
      )}
      {metadata.description && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.request')}</span>
          <span className="status-card__detail-value">
            {metadata.description.length > 80 ? metadata.description.slice(0, 80) + '…' : metadata.description}
          </span>
        </div>
      )}
      {metadata.scheduledFor && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.scheduled')}</span>
          <span className="status-card__detail-value">{metadata.scheduledFor}</span>
        </div>
      )}
      {metadata.roomNumber && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('common.room')}</span>
          <span className="status-card__detail-value">{metadata.roomNumber}</span>
        </div>
      )}
    </>
  );
}

function LaundryCardBody({ metadata }: { metadata: StatusCardMetadata }) {
  const { t } = useTranslation();
  const sym = metadata.currency || '₪';
  return (
    <>
      {metadata.isExpress && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.service')}</span>
          <span className="status-card__detail-value">⚡ {t('status_card.express')}</span>
        </div>
      )}
      {metadata.items && metadata.items.length > 0 && (
        <div className="status-card__items">
          {metadata.items.map((item, i) => (
            <div key={i} className="status-card__item">
              <span>{item.quantity}× {item.name}</span>
              {item.price != null && <span className="status-card__item-price">{sym}{item.price.toFixed(2)}</span>}
            </div>
          ))}
        </div>
      )}
      {metadata.expressSurcharge != null && metadata.expressSurcharge > 0 && (
        <div className="status-card__detail">
          <span className="status-card__detail-label">{t('status_card.expressSurcharge')}</span>
          <span className="status-card__detail-value">{sym}{metadata.expressSurcharge.toFixed(2)}</span>
        </div>
      )}
      {metadata.totalAmount != null && (
        <div className="status-card__total">
          <span>{t('common.total')}</span>
          <span>{sym}{metadata.totalAmount.toFixed(2)}</span>
        </div>
      )}
    </>
  );
}
